import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Plus, Trash2, X, Pencil, FileDown, ChevronDown, MapPin, User, CalendarDays, CalendarPlus, FileText, Upload } from "lucide-react";
import EmptyState from "../components/EmptyState";
import AktivitaDetail from "../components/AktivitaDetail";
import PripadForm from "../components/PripadForm";
import NovaAktivita from "../components/NovaAktivita";
import { Button, Spinner, Field } from "../components/ui/kit";
import { etapa, fazeBarva, nazevEtapy } from "../lib/faze";
import TechKroky, { Dialog, DUVODY } from "../components/TechKroky";
import { fmtKc, fmtDateTime } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { SwipeAktivita, SwipeNapoveda, useAktivitaSwipe } from "../components/AktivitaSwipe";
import { canSee, canEditAktivita } from "../lib/nav";
import { useJeMobil } from "../lib/mobil";
import { VyberVyrobce, useCenikVyber } from "../components/VyberZCeniku";
import api, { apiError } from "../lib/api";
import { SkupinyAktivit } from "../lib/aktivity";
import { Drobecky, useNazevStranky, useUrlStav } from "../lib/navigace";
import { pripravOkno, otevritPdf, zavritOkno, textOkna, TEXT_PRIPRAVA, dlouhoCeka } from "../lib/pdf";
import PdfPripraveno, { PdfCeka } from "../components/PdfPripraveno";
import Vyber from "../components/Vyber";
import PasFazi from "../components/PasFazi";
import DetailHlava, { KartaZalozky, KartaSekce, Pridat, StitekTmavy } from "../components/DetailHlava";
import BudovaPripadu from "../components/BudovaPripadu";
import RadekAktivity from "../components/RadekAktivity";
import SlozkaDokumentu from "../components/SlozkaDokumentu";
import { infoSluzby } from "../lib/odkazy";
import KdoUvidi, { PopisViditelnosti } from "../components/KdoUvidi";
import PripadEkonomika from "../components/PripadEkonomika";
import { ikonaTechnologie } from "../lib/technologie";

/** Barvy technologií podle prototypu (OP_TECH). Klíč je název kategorie. */
const TECH_BARVA = {
  "Rekuperace": "#2c6ecb",
  "Tepelné čerpadlo": "#17876a",
  "Fotovoltaika": "#dc9a2c",
  "Klimatizace": "#7c4dd1",
  "Podlahové topení": "#c8551a",
  "Chytrá domácnost": "#4a5563",
  "Bojler": "#1f8fb3",
};
const TECH_VYCHOZI = "#8b8f96";


const DOK_BARVA = {
  PDF: { bg: "#fdeceb", col: "#a32418" },
  DWG: { bg: "#eef4fd", col: "#24589f" },
  DOC: { bg: "#eef4fd", col: "#24589f" },
  DOCX: { bg: "#eef4fd", col: "#24589f" },
  XLSX: { bg: "#eaf6f1", col: "#0f6b53" },
  JPG: { bg: "#eaf6f1", col: "#0f6b53" },
  JPEG: { bg: "#eaf6f1", col: "#0f6b53" },
  PNG: { bg: "#eaf6f1", col: "#0f6b53" },
  HEIC: { bg: "#eaf6f1", col: "#0f6b53" },
};

function datumRok(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : `${d.getDate()}. ${d.getMonth() + 1}. ${d.getFullYear()}`;
}

/** 412 kB / 2,8 MB — v desetinné čárce, protože je to česká aplikace. */
function velikost(b) {
  const n = Number(b) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} kB`;
  return `${(n / (1024 * 1024)).toFixed(1).replace(".", ",")} MB`;
}

function pocet(n, jedna, dve, pet) {
  if (n === 1) return `1 ${jedna}`;
  if (n >= 2 && n <= 4) return `${n} ${dve}`;
  return `${n} ${pet}`;
}

function TechForm({ onClose, onAdd }) {
  const [kats, setKats] = useState([]);
  const { vyrobci } = useCenikVyber();
  const [f, setF] = useState({ kategorie_id: "", vyrobce: "", vyrobce_id: null, hodnota_kc: 0 });
  const [jiny, setJiny] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    api.get("/api/meta/ciselniky").then((r) => {
      setKats(r.data.kategorie || []);
      // Předvybraná technologie firmy (Nastavení → Technologie), jinak nejčastější, jinak první.
      setF((x) => ({ ...x, kategorie_id: r.data.vychozi_kategorie_id || r.data.kategorie?.[0]?.id || "" }));
    });
  }, []);
  // Změna technologie: výrobce, který pro ni v ceníku nic nemá, se odvybere.
  const zmenKategorii = (kid) => {
    setF((x) => {
      const v = vyrobci.find((y) => y.id === x.vyrobce_id);
      const zustava = v && (v.kategorie_ids || []).includes(kid);
      return { ...x, kategorie_id: kid, ...(zustava || jiny ? {} : { vyrobce_id: null, vyrobce: "" }) };
    });
  };
  const submit = async (e) => {
    e.preventDefault(); setSaving(true); setError("");
    try { await onAdd({ ...f, vyrobce: f.vyrobce.trim(), hodnota_kc: Number(f.hodnota_kc) || 0 }); onClose(); }
    catch (err) { setError(apiError(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex md:items-center items-end justify-center md:p-4" data-testid="tech-form">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white w-full md:max-w-[440px] md:rounded-card rounded-t-sheet shadow-panel">
        <div className="border-b border-line-softer px-6 py-4 flex items-center justify-between">
          <h2 className="text-[16px] font-800">Přidat technologii</h2>
          <button onClick={onClose} className="text-muted hover:text-ink"><X size={20} /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <Field label="Kategorie">
            <Vyber className="field" value={f.kategorie_id} onChange={(e) => zmenKategorii(e.target.value)} data-testid="tech-kategorie">
              {kats.map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}
            </Vyber>
          </Field>
          <Field label="Výrobce">
            <VyberVyrobce vyrobci={vyrobci} kategorieId={f.kategorie_id} vyrobceId={f.vyrobce_id} vyrobce={f.vyrobce} jiny={jiny}
              volnyText testid="tech-vyrobce" onChange={({ vyrobce_id, vyrobce, jiny: j }) => { setJiny(j); setF((x) => ({ ...x, vyrobce_id, vyrobce })); }} />
          </Field>
          {/* Nová technologie začíná jako poptávka (Nový); dál ji posouvá nabídka. */}
          <Field label="Odhad hodnoty (Kč)" hint="Orientační. Po schválení nabídky se nahradí cenou z nabídky.">
            <input type="number" className="field amount" value={f.hodnota_kc} onChange={(e) => setF({ ...f, hodnota_kc: e.target.value })} data-testid="tech-hodnota" />
          </Field>
          {error && <div className="text-[12.5px] text-danger">{error}</div>}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="btn-ghost">Zrušit</button>
            <Button disabled={saving} data-testid="tech-save">{saving ? <Spinner /> : "Přidat"}</Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Výběr osoby v hlavičce případu: vypadá jako text, klikne se na něj.
// Nativní select je přes text průhledný: šířka pak odpovídá vybranému
// jménu, ne nejdelší možnosti, a na mobilu se otevře systémový výběr.
function OsobaVyber({ label, value, jmeno, tym, onChange, povinne = false, testid }) {
  const vybrany = tym.find((m) => m.id === value)?.name || jmeno || (povinne ? "…" : "nikdo");
  return (
    <span className="inline-flex items-center gap-1">
      {label}
      <span className="relative inline-flex items-center gap-0.5 font-600 text-white hover:text-white/80">
        {vybrany}<ChevronDown size={13} className="text-muted-3" />
        <select value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} aria-label={label}
          className="absolute inset-0 w-full opacity-0 cursor-pointer text-[16px]">
          {!povinne && <option value="">nikdo</option>}
          {value && !tym.some((m) => m.id === value) && <option value={value}>{jmeno || "…"}</option>}
          {tym.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
        </select>
      </span>
    </span>
  );
}

export default function PripadDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const canWrite = canSee(user, ["OWNER", "OBCHODNIK"]);
  const isOwner = canSee(user, ["OWNER"]);
  const [data, setData] = useState(undefined);
  const [addTech, setAddTech] = useState(false);
  const [newAct, setNewAct] = useState(false);
  const [uprava, setUprava] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [editAct, setEditAct] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfOdkaz, setPdfOdkaz] = useState(null);
  const [pdfCeka, setPdfCeka] = useState(false);   // po aktualizaci se tisk PDF chvíli zprovozňuje
  const [nahrava, setNahrava] = useState(false);
  const [chyba, setChyba] = useState("");
  const [novyOdkaz, setNovyOdkaz] = useState(null);   // {url, nazev, komu, ai} = formulář otevřený
  const [upravaOdkazu, setUpravaOdkazu] = useState(null); // {id, komu, ai}
  const [zalozka, setZalozka] = useUrlStav("zalozka", "technologie");
  const souborRef = useRef(null);
  const backend = process.env.REACT_APP_BACKEND_URL;

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/api/obchodni-pripady/${id}`); setData(data); }
    catch { setData(null); }
  }, [id]);
  useEffect(() => { load(); }, [load]);
  useNazevStranky(data?.pripad && `${data.pripad.cislo} ${data.pripad.nazev}`);

  // Obchodník a vedoucí montáže — komu workflow přiřazuje úkoly.
  const [tym, setTym] = useState([]);
  useEffect(() => {
    if (!canWrite) return;
    api.get("/api/team").then((r) => setTym((r.data.members || []).filter((m) => m.active && m.role !== "SUBDODAVATEL"))).catch(() => {});
  }, [canWrite]);
  const zmenOsobu = async (pole, v) => {
    setChybaPripadu("");
    try { await api.patch(`/api/obchodni-pripady/${id}`, { [pole]: v || "" }); }
    catch (e) { setChybaPripadu(apiError(e.response?.data?.detail) || e.message); }
    load();
  };

  // Nad celým případem jde ručně jen Ztraceno a Dokončeno — je to hromadná
  // akce nad technologiemi, fázi případu pak dopočítá server.
  const [ztrataPripadu, setZtrataPripadu] = useState(null);   // {duvod, poznamka}
  const [chybaPripadu, setChybaPripadu] = useState("");
  const changeFaze = async (f, extra = {}) => {
    if (f === "DOKONCENO" && !window.confirm(
      "Předat celý případ? Každá technologie v montáži musí mít podepsaný předávací protokol.")) return;
    setChybaPripadu("");
    try { await api.post(`/api/obchodni-pripady/${id}/faze`, { faze: f, ...extra }); setZtrataPripadu(null); }
    catch (e) { setChybaPripadu(apiError(e.response?.data?.detail) || e.message); }
    load();
  };
  const addTechnologie = async (payload) => { await api.post(`/api/obchodni-pripady/${id}/technologie`, payload); load(); };
  const delTech = async (tid) => { if (window.confirm("Odebrat technologii?")) { await api.delete(`/api/technologie/${tid}`); load(); } };
  const toggleTask = async (a) => { await api.post(`/api/aktivity/${a.id}/hotovo`); load(); };
  const mobil = useJeMobil();
  const swipe = useAktivitaSwipe({ reload: load, onUpravit: setEditAct });

  const nahrajSoubory = async (files) => {
    if (!files?.length) return;
    setNahrava(true); setChyba("");
    const fd = new FormData();
    [...files].forEach((f) => fd.append("files", f));
    try {
      await api.post(`/api/obchodni-pripady/${id}/soubory`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      load();
    } catch (e) {
      setChyba(apiError(e.response?.data?.detail) || e.message);
    } finally {
      setNahrava(false);
    }
  };
  const pridatOdkaz = async (e) => {
    e.preventDefault();
    setChyba("");
    try { await api.post(`/api/obchodni-pripady/${id}/odkazy`, novyOdkaz); setNovyOdkaz(null); load(); }
    catch (err) { setChyba(apiError(err.response?.data?.detail) || err.message); }
  };
  const ulozitOdkaz = async () => {
    setChyba("");
    try {
      await api.patch(`/api/obchodni-pripady/${id}/odkazy/${upravaOdkazu.id}`, { komu: upravaOdkazu.komu, ai: upravaOdkazu.ai });
      setUpravaOdkazu(null); load();
    } catch (err) { setChyba(apiError(err.response?.data?.detail) || err.message); }
  };
  const smazOdkaz = async (oid) => {
    if (!window.confirm("Odebrat odkaz? Soubor na disku firmy zůstane.")) return;
    await api.delete(`/api/obchodni-pripady/${id}/odkazy/${oid}`); load();
  };
  const smazSoubor = async (sid) => {
    if (!window.confirm("Smazat dokument?")) return;
    await api.delete(`/api/pripad-soubory/${sid}`); load();
  };

  const exportSouhrn = async () => {
    // Záložka se otevře hned při kliknutí — později by ji prohlížeč zablokoval (lib/pdf.js).
    const okno = pripravOkno();
    setPdfBusy(true); setPdfOdkaz(null);
    const chyba = (text) => { zavritOkno(okno); setPdfBusy(false); window.alert(text); };
    try {
      const { data: j } = await api.post(`/api/obchodni-pripady/${id}/pdf-souhrn`, { s_cenami_polozek: true });
      const zacatek = Date.now();
      let hlaseno = false;
      const poll = setInterval(async () => {
        try {
          const r = await api.get(`/api/pdf-jobs/${j.job_id}`);
          if (r.data.status === "processing" && !hlaseno && dlouhoCeka(r.data, zacatek)) {
            hlaseno = true; setPdfCeka(true); textOkna(okno, TEXT_PRIPRAVA);
          }
          if (r.data.status !== "processing") setPdfCeka(false);
          if (r.data.status === "done") {
            clearInterval(poll); setPdfBusy(false);
            const url = `${backend}${r.data.result.url}`;
            if (!otevritPdf(okno, url)) setPdfOdkaz(url);
          }
          else if (r.data.status === "error") { clearInterval(poll); chyba(r.data.detail || "PDF se nepodařilo vytvořit."); }
        } catch (e) { clearInterval(poll); setPdfCeka(false); chyba(apiError(e.response?.data?.detail) || "PDF se nepodařilo vytvořit."); }
      }, 1500);
    } catch (e) { chyba(apiError(e.response?.data?.detail)); }
  };

  if (data === undefined) return <div className="p-10 flex justify-center text-muted"><Spinner /></div>;
  if (data === null) return <div className="card p-8"><EmptyState icon="FileX" title="Případ nenalezen" description="Případ neexistuje nebo byl archivován." action={<Button variant="ghost" onClick={() => nav("/obchod")}>Zpět na obchod</Button>} /></div>;

  const p = data.pripad;
  const money = data.muze_ceny;
  const adr = p.adresa || {};
  const zapocitane = p.technologie.filter((t) => t.faze !== "ZTRACENO");
  // Důvod ztráty případu: uložený u případu, u starších dat z poslední ztracené technologie.
  const duvodZtraty = p.ztraceno?.duvod ? p.ztraceno
    : p.technologie.map((t) => t.ztraceno).filter((z) => z && z.duvod)
      .sort((a, b) => String(b.kdy || "").localeCompare(String(a.kdy || "")))[0] || null;
  const ukoly = p.ukoly || [];
  const poTerminu = ukoly.filter((a) => a.po_terminu).length;
  const otevrene = ukoly.filter((a) => a.stav === "OTEVRENA").length;
  const soubory = p.soubory || [];
  const odkazy = p.odkazy || [];
  // Odkazy na disk firmy smí přidat i technik z terénu (fotky, revize).
  const muzeOdkazy = canSee(user, ["OWNER", "OBCHODNIK", "TECHNIK"]);

  const hotoveUkoly = ukoly.length - otevrene;
  const prazdnaAdresa = !(adr.mesto || adr.ulice);
  const nahratSoubor = () => souborRef.current?.click();
  const akceZalozky = {
    technologie: canWrite ? <Pridat text="Technologie" onClick={() => setAddTech(true)} testid="add-tech" /> : null,
    dokumenty: muzeOdkazy ? <Pridat text="Odkaz" onClick={() => setNovyOdkaz({ url: "", nazev: "", komu: [], ai: false })} testid="odkaz-pridat-hlava" /> : null,
    ekonomika: null,
    historie: null,
  }[zalozka];
  // Ekonomika (faktury, náklady, výsledek) jen pro ty, kdo vidí peníze a obchod.
  const sEkonomikou = money && canWrite;
  const aktivniZalozka = zalozka === "ekonomika" && !sEkonomikou ? "technologie" : zalozka;

  return (
    <div data-testid="pripad-detail" className="flex flex-col gap-4">
      <Drobecky cesta={[{ nazev: "Obchodní případy", to: "/obchod", testid: "pripad-zpet" }]} aktualni={p.nazev} />

      <DetailHlava testid="pripad-hlava" druh="Obchodní případ" nazev={p.nazev}
        stitky={<>
          <StitekTmavy><span className="amount">{p.cislo}</span></StitekTmavy>
          {p.faze === "ZTRACENO" && <StitekTmavy barva="#ff8a7a">Ztraceno</StitekTmavy>}
        </>}
        meta={[
          p.client && (
            <button onClick={() => nav(`/klienti/${p.client.id}`)} data-testid="pripad-client-link"
              className="inline-flex items-center gap-1.5 font-600 text-white hover:text-white/80"><User size={13} />{p.client.display_name}</button>
          ),
          !prazdnaAdresa ? (
            <span className="inline-flex items-center gap-1.5" data-testid="pripad-adresa">
              <MapPin size={13} />{[adr.ulice, [adr.psc, adr.mesto].filter(Boolean).join(" ")].filter(Boolean).join(", ")}
            </span>
          ) : canWrite && (
            <button onClick={() => setUprava(true)} className="inline-flex items-center gap-1.5 font-600 text-[#ffb182]" data-testid="pripad-doplnit-adresu">
              <MapPin size={13} /> Doplnit adresu realizace
            </button>
          ),
          canWrite ? (
            <OsobaVyber label="Obchodník" value={p.obchodnik_id} jmeno={p.obchodnik} tym={tym} povinne
              onChange={(v) => zmenOsobu("obchodnik_id", v)} testid="pripad-obchodnik" />
          ) : p.obchodnik && <span>Obchodník <span className="font-600 text-white">{p.obchodnik}</span></span>,
          canWrite ? (
            <OsobaVyber label="Vedoucí montáže" value={p.vedouci_montaze_id} jmeno={p.vedouci_montaze} tym={tym}
              onChange={(v) => zmenOsobu("vedouci_montaze_id", v)} testid="pripad-vedouci" />
          ) : p.vedouci_montaze && <span>Vedoucí montáže <span className="font-600 text-white">{p.vedouci_montaze}</span></span>,
          <span className="inline-flex items-center gap-1.5"><CalendarDays size={13} />Založeno <span className="amount">{datumRok(p.created_at)}</span></span>,
        ].filter(Boolean)}
        vedlejsi={canWrite ? [{ text: "Aktivita", ikona: Plus, onClick: () => setNewAct(true), testid: "pripad-new-activity" }] : []}
        hlavni={p.nabidka ? { text: "Otevřít nabídku", onClick: () => nav(`/nabidky/${p.nabidka.id}`), testid: "pripad-nabidka" } : null}
        vice={[
          ...(canWrite ? [{ text: "Upravit název a adresu", ikona: Pencil, onClick: () => setUprava(true), testid: "pripad-upravit" }] : []),
          ...(money ? [{ text: pdfBusy ? "Připravuji PDF…" : "Souhrn případu do PDF", ikona: FileDown, onClick: exportSouhrn, testid: "pdf-souhrn-btn" }] : []),
          ...(isOwner ? [{ text: "Archivovat případ", ikona: Trash2, nebezpecne: true, testid: "pripad-delete",
            onClick: async () => { if (window.confirm("Archivovat případ?")) { await api.delete(`/api/obchodni-pripady/${id}`); nav("/obchod"); } } }] : []),
        ]}
        cta={[
          ...(p.nabidka ? [{ text: "Nabídka", ikona: FileText, onClick: () => nav(`/nabidky/${p.nabidka.id}`), testid: "pripad-cta-nabidka" }] : []),
          ...(canWrite ? [{ text: "Aktivita", ikona: CalendarPlus, onClick: () => setNewAct(true), testid: "pripad-cta-aktivita" }] : []),
          ...(canWrite ? [{ text: "Upravit", ikona: Pencil, onClick: () => setUprava(true), testid: "pripad-cta-upravit" }] : []),
        ]}
        dlazdice={[
          ...(money ? [{ popisek: "Součet případu", hodnota: fmtKc(p.hodnota || 0), jednotka: "Kč", testid: "dl-soucet",
            pod: `${p.hodnota_s_dph != null ? `s DPH ${fmtKc(p.hodnota_s_dph)} Kč · ` : ""}${pocet(zapocitane.length, "technologie", "technologií", "technologií")}` }] : []),
          { popisek: "Kde to stojí", hodnota: nazevEtapy(etapa(p)) || "—", testid: "dl-faze",
            zvyraznit: p.faze === "ZTRACENO" ? "danger" : fazeBarva(etapa(p)).dot,
            pod: p.technologii > 0 ? `rozhodnuto ${p.rozhodnuto} / ${p.technologii}` : "zatím bez technologie" },
          { popisek: "Otevřené aktivity", hodnota: String(otevrene), testid: "dl-aktivity",
            zvyraznit: poTerminu ? "danger" : undefined,
            pod: poTerminu ? `${poTerminu}× po termínu` : `${hotoveUkoly} hotových` },
        ]} />

      {/* kde to stojí — přes celou šířku */}
      <div className="card px-4 md:px-5 py-4" data-testid="pripad-kde-to-stoji">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <h2 className="t-sekce text-ink font-700">Kde to stojí</h2>
          <span className="t-meta text-muted-2 hidden md:inline">podle nejpomalejší technologie</span>
          {p.technologii > 0 && (
            <span className="t-meta font-600 text-sec amount" data-testid="rozhodnuto">rozhodnuto {p.rozhodnuto} / {p.technologii}</span>
          )}
          {canWrite && !["DOKONCENO", "ZTRACENO"].includes(p.faze) && (
            <div className="ml-auto flex gap-1.5">
              {/* Předat vše jen když jsou všechny živé technologie v montáži nebo
                  předané — jinak by server akci po potvrzení odmítl. */}
              {p.technologie.some((t) => t.faze === "REALIZACE")
                && p.technologie.every((t) => ["REALIZACE", "DOKONCENO", "ZTRACENO"].includes(t.faze)) && (
                <button onClick={() => changeFaze("DOKONCENO")} data-testid="set-faze-DOKONCENO"
                  className="tap md:min-h-0 pill border px-3 h-9 md:h-8 t-meta font-600 bg-white text-sec-3 border-line-strong hover:bg-inputbg">
                  Předat vše
                </button>
              )}
              <button onClick={() => setZtrataPripadu({ duvod: "", poznamka: "" })} data-testid="set-faze-ZTRACENO"
                className="tap md:min-h-0 pill border px-3 h-9 md:h-8 t-meta font-600 bg-white text-sec-3 border-line-strong hover:bg-inputbg hover:text-danger">
                Ztraceno
              </button>
            </div>
          )}
          {p.faze === "DOKONCENO" && (
            <span className="ml-auto label-up px-2.5 py-1 rounded-pill bg-success-bg text-success">Předáno</span>
          )}
        </div>
        <div className="mt-3">
          <PasFazi faze={etapa(p)} kdy={p.faze_kdy || {}} />
        </div>
        {p.faze === "ZTRACENO" && duvodZtraty && (
          // Proč jsme případ ztratili — důvod i poznámka obchodníka, ne jen štítek.
          <div className="mt-3 rounded-[14px] bg-danger/5 shadow-[inset_0_0_0_1px_rgba(217,58,43,.18)] px-3.5 py-3" data-testid="pripad-duvod-ztraty">
            <span className="label-up text-danger block">Proč ztraceno</span>
            <span className="block t-telo font-700 text-ink mt-1">{DUVODY[duvodZtraty.duvod] || duvodZtraty.duvod}</span>
            {duvodZtraty.poznamka && (
              <span className="block t-telo text-sec mt-0.5 whitespace-pre-line break-words" data-testid="pripad-poznamka-ztraty">{duvodZtraty.poznamka}</span>
            )}
          </div>
        )}
        {chybaPripadu && <div className="t-meta text-danger mt-2" data-testid="chyba-pripadu">{chybaPripadu}</div>}
      </div>

      {ztrataPripadu && (
        <Dialog titulek="Případ je ztracený" onClose={() => setZtrataPripadu(null)} testid="dialog-ztrata-pripadu">
          <p className="t-meta text-muted">Všechny nepředané technologie se označí jako ztracené, jejich otevřené úkoly se zruší a rozpracované nabídky zamítnou.</p>
          <div>
            <label htmlFor="ztrata-pripadu-duvod" className="label-up text-muted block mb-1.5">Důvod</label>
            <Vyber id="ztrata-pripadu-duvod" className="field" value={ztrataPripadu.duvod}
              onChange={(e) => setZtrataPripadu({ ...ztrataPripadu, duvod: e.target.value })} data-testid="ztrata-pripadu-duvod">
              <option value="">Vyber důvod…</option>
              {Object.entries(DUVODY).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </Vyber>
          </div>
          <div>
            <label htmlFor="ztrata-pripadu-poznamka" className="label-up text-muted block mb-1.5">Poznámka (nepovinná)</label>
            <input id="ztrata-pripadu-poznamka" className="field" value={ztrataPripadu.poznamka}
              onChange={(e) => setZtrataPripadu({ ...ztrataPripadu, poznamka: e.target.value })} />
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setZtrataPripadu(null)} className="btn-ghost">Zpět</button>
            <button disabled={!ztrataPripadu.duvod} onClick={() => changeFaze("ZTRACENO", ztrataPripadu)} className="btn-primary"
              data-testid="ztrata-pripadu-potvrdit">Označit jako ztracený</button>
          </div>
        </Dialog>
      )}

      {/* Náklad přiřazený jen na zakázku se do marže technologie nedostane. */}
      {money && p.naklady_bez_technologie > 0 && (
        <button onClick={() => nav("/doklady?bez_zakazky=0")} data-testid="naklady-bez-technologie"
          className="card w-full text-left px-5 py-4 flex items-center gap-3 hover:bg-inputbg">
          <span className="h-2 w-2 rounded-full bg-accent shrink-0" />
          <span className="min-w-0 flex-1">
            <span className="block t-telo font-700 text-ink">
              <span className="amount">{fmtKc(p.naklady_bez_technologie)} Kč</span> nákladů bez technologie
            </span>
            <span className="block t-meta text-muted mt-0.5">
              Sedí v zakázce, ale nepočítají se do marže žádné technologie. Doplň je v dokladech.
            </span>
          </span>
          <span className="t-meta font-600 text-accent shrink-0">Doklady →</span>
        </button>
      )}

      {/* Počítač: vlevo záložky, vpravo aktivity. Telefon: aktivity první (práce v terénu). */}
      <div className="grid lg:grid-cols-[minmax(0,1fr)_380px] gap-4 items-start">
        <div className="min-w-0 lg:col-start-2 lg:row-start-1">
          <KartaSekce testid="pripad-aktivity" titul="Aktivity" pocet={ukoly.length || null}
            akce={canWrite && <Pridat text="Aktivita" onClick={() => setNewAct(true)} testid="pripad-add-task" />}>
            {ukoly.length === 0 ? (
              <EmptyState icon="ListTodo" title="Žádné aktivity" description="Přidej aktivitu navázanou na tento případ." />
            ) : mobil ? (
              <div className="p-3 pt-0 space-y-2" data-testid="ukoly-swipe">
                <SwipeNapoveda smiUpravit={ukoly.some((a) => canEditAktivita(user, a.oblast))} />
                <SkupinyAktivit kompakt items={ukoly} render={(a) => (
                  <SwipeAktivita key={a.id} a={a} onOpen={(x) => setDetailId(x.id)}
                    smiUpravit={canEditAktivita(user, a.oblast)} {...swipe.swipeProps} />
                )} />
              </div>
            ) : <div className="border-t border-line"><SkupinyAktivit items={ukoly} render={(a) => (
              <RadekAktivity key={a.id} a={a} kompakt onOpen={() => setDetailId(a.id)}
                onToggle={canEditAktivita(user, a.oblast) ? () => toggleTask(a) : null} />
            )} /></div>}
          </KartaSekce>
        </div>

        <div className="min-w-0 lg:col-start-1 lg:row-start-1">
          <KartaZalozky testid="pripad-zalozky" aktivni={aktivniZalozka} onZmena={setZalozka} akce={akceZalozky}
            zalozky={[
              { key: "technologie", label: "Technologie", pocet: p.technologie.length, testid: "pripad-tab-technologie" },
              ...(sEkonomikou ? [{ key: "ekonomika", label: "Ekonomika", testid: "pripad-tab-ekonomika" }] : []),
              { key: "budova", label: "Budova", testid: "pripad-tab-budova" },
              { key: "dokumenty", label: "Dokumenty", pocet: soubory.length + odkazy.length + (p.slozka_url ? 1 : 0), testid: "pripad-tab-dokumenty" },
              { key: "historie", label: "Historie", pocet: p.udalosti.length, testid: "pripad-tab-historie" },
            ]}>
            {aktivniZalozka === "technologie" && (
              <div className="rise">
                {p.technologie.length === 0 ? (
                  <EmptyState icon="Package" title="Zatím žádné technologie"
                    description="Přidej technologie, ze kterých se spočítá hodnota případu." />
                ) : p.technologie.map((t) => {
                  const ztraceno = t.faze === "ZTRACENO";
                  const stav = fazeBarva(etapa(t));
                  // Barva technologie ze serveru (Nastavení → Technologie), stejná jako v PDF nabídky.
                  const barvaTech = t.kategorie_barva || TECH_BARVA[t.kategorie_name] || TECH_VYCHOZI;
                  const IkonaTech = ikonaTechnologie(t.kategorie_icon);
                  return (
                    <div key={t.id} data-testid={`tech-${t.id}`}
                      className={`px-4 md:px-5 py-3 flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-line-soft last:border-0 ${
                        ztraceno ? "opacity-60" : ""}`}>
                      <span className="order-1 h-9 w-9 rounded-[12px] inline-flex items-center justify-center shrink-0"
                        style={{ background: `${barvaTech}1a`, color: barvaTech }}><IkonaTech size={17} strokeWidth={1.9} /></span>
                      <button onClick={() => nav(`/technologie/${t.id}`)} className="order-2 min-w-0 flex-1 text-left">
                        <span className="block t-telo font-700 text-ink truncate">{t.kategorie_name}</span>
                        {ztraceno && t.ztraceno ? (
                          // Důvod ztráty přímo u technologie — ne jen v historii případu.
                          <span className="block t-meta text-danger break-words" data-testid={`tech-duvod-${t.id}`}
                            title={t.ztraceno.poznamka || ""}>
                            Ztraceno · {DUVODY[t.ztraceno.duvod] || t.ztraceno.duvod}{t.ztraceno.poznamka ? ` — ${t.ztraceno.poznamka}` : ""}
                          </span>
                        ) : (
                          <span className="block t-meta text-muted truncate">{t.vyrobce || "výrobce neuveden"}</span>
                        )}
                      </button>
                      {/* Telefon: fáze a úpravy na druhém řádku pod názvem; počítač: vše v jednom řádku. */}
                      <span className="order-4 basis-full pl-12 flex items-center gap-2 md:contents">
                        <span className="md:order-3 label-up px-2.5 py-1 rounded-pill shrink-0" data-testid={`tech-faze-${t.id}`}
                          style={{ color: stav.col, background: stav.bg }}>{nazevEtapy(etapa(t))}</span>
                        {canWrite && (
                          <span className="md:order-5 ml-auto md:ml-0 flex items-center gap-1 shrink-0">
                            <button onClick={() => nav(`/technologie/${t.id}`)} title="Upravit technologii" aria-label="Upravit technologii"
                              className="tap md:min-h-0 md:min-w-0 h-8 w-8 rounded-full bg-inputbg flex items-center justify-center text-sec hover:text-ink">
                              <Pencil size={13} />
                            </button>
                            <button onClick={() => delTech(t.id)} data-testid={`del-tech-${t.id}`} aria-label="Odebrat technologii"
                              className="tap md:min-h-0 md:min-w-0 text-muted-2 hover:text-danger px-1"><X size={15} /></button>
                          </span>
                        )}
                      </span>
                      {/* Kroky technologie (brány hlídá server): na vlastním řádku pod názvem. */}
                      {(t.akce || []).some((a) => a.smi) && (
                        <span className="flex flex-wrap items-center gap-1.5 order-last basis-full pl-12 md:order-last">
                          <TechKroky compact tech={t} pripad={p} isOwner={isOwner} onChanged={load} />
                        </span>
                      )}
                      {money && (
                        <span className={`order-3 md:order-4 amount t-telo font-700 md:w-[120px] text-right shrink-0 whitespace-nowrap ${
                          ztraceno ? "text-muted line-through" : "text-ink"}`}
                          title={t.hodnota_z_nabidky ? "Cena ze schválené nabídky" : "Odhad"}>
                          {fmtKc(t.hodnota_kc || 0)} Kč
                          {!t.hodnota_z_nabidky && !ztraceno && <span className="block t-meta text-muted-2 font-500">odhad</span>}
                        </span>
                      )}
                    </div>
                  );
                })}
                {money && p.technologie.length > 0 && (
                  <div className="px-4 md:px-5 py-3.5 bg-[#fafaf8] border-t border-line flex flex-wrap items-baseline gap-x-4 gap-y-1"
                    data-testid="pripad-finance">
                    <span className="label-up text-muted">Součet případu</span>
                    <span className="ml-auto text-right">
                      <span className="amount text-[18px] font-800 text-ink">{fmtKc(p.hodnota || 0)} Kč</span>
                      <span className="t-meta text-muted font-600"> bez DPH</span>
                      <span className="block t-meta text-muted">
                        {p.hodnota_s_dph != null && <>s DPH <span className="amount">{fmtKc(p.hodnota_s_dph)} Kč</span> · </>}
                        z {pocet(zapocitane.length, "technologie", "technologií", "technologií")}
                        {p.technologie.length !== zapocitane.length ? " · ztracené se nepočítají" : ""}
                      </span>
                    </span>
                  </div>
                )}
              </div>
            )}

            {aktivniZalozka === "ekonomika" && <PripadEkonomika pripadId={id} nazev={`${p.cislo} ${p.nazev}`} />}
            {aktivniZalozka === "budova" && (
              <BudovaPripadu pripadId={id} budova={p.budova} muzeUpravit={muzeOdkazy}
                onUlozeno={(budova) => setData((d) => ({ ...d, pripad: { ...d.pripad, budova } }))} />
            )}

            {aktivniZalozka === "dokumenty" && (
              <div className="rise" data-testid="pripad-dokumenty">
                {/* Dokumenty zakázky žijí na disku firmy (SharePoint, OneDrive, Google Disk) —
                    tady je složka případu (nebo klienta) a odkazy na jednotlivé soubory. */}
                <div className="px-4 md:px-5 py-3.5 border-b border-line-soft">
                  <SlozkaDokumentu url={p.slozka_url} komu={p.slozka_komu || []} ai={p.slozka_ai}
                    testid="pripad-slozka" popisek="Složka případu"
                    zdedena={p.client?.slozka_url ? { url: p.client.slozka_url, label: "Složka klienta", komu: p.client.slozka_komu, ai: p.client.slozka_ai } : null}
                    onUlozit={canWrite ? async (zmena) => { await api.patch(`/api/obchodni-pripady/${id}`, zmena); load(); } : null} />
                </div>
                {odkazy.map((o) => {
                  const s = infoSluzby(o.url);
                  return (
                    <React.Fragment key={o.id}>
                    <div className="px-4 md:px-5 py-3 flex items-center gap-3 border-b border-line-soft" data-testid={`odkaz-${o.id}`}>
                      <span className="h-9 w-9 rounded-[10px] amount text-[9.5px] font-700 flex items-center justify-center shrink-0"
                        style={{ background: s.bg, color: s.barva }}>{s.zkratka}</span>
                      <a href={o.url} target="_blank" rel="noopener noreferrer" className="min-w-0 flex-1">
                        <span className="block t-telo md:text-[13.5px] font-600 text-ink truncate">{o.nazev}</span>
                        <span className="block t-meta text-muted-2">{s.nazev} · {datumRok(o.created_at)}<PopisViditelnosti komu={o.komu} ai={o.ai} /></span>
                      </a>
                      {(o.created_by === user?.id || isOwner) && (
                        <button onClick={() => setUpravaOdkazu(upravaOdkazu?.id === o.id ? null : { id: o.id, komu: o.komu || [], ai: !!o.ai })}
                          data-testid={`uprav-odkaz-${o.id}`} aria-label="Kdo odkaz uvidí" title="Kdo odkaz uvidí"
                          className="tap md:min-h-0 md:min-w-0 h-8 w-8 rounded-full bg-inputbg inline-flex items-center justify-center text-sec hover:text-ink shrink-0">
                          <Pencil size={13} />
                        </button>
                      )}
                      {muzeOdkazy && (
                        <button onClick={() => smazOdkaz(o.id)} data-testid={`del-odkaz-${o.id}`} aria-label="Odebrat odkaz"
                          className="tap md:min-h-0 md:min-w-0 text-muted-2 hover:text-danger shrink-0"><X size={15} /></button>
                      )}
                    </div>
                    {upravaOdkazu?.id === o.id && (
                      <div className="px-4 md:px-5 pb-3.5 pt-1 border-b border-line-soft space-y-2.5" data-testid={`odkaz-uprava-${o.id}`}>
                        <KdoUvidi komu={upravaOdkazu.komu} onKomu={(komu) => setUpravaOdkazu({ ...upravaOdkazu, komu })}
                          ai={upravaOdkazu.ai} onAi={(ai) => setUpravaOdkazu({ ...upravaOdkazu, ai })} testid={`odkaz-kdo-${o.id}`} />
                        <div className="flex items-center gap-2">
                          <button type="button" onClick={ulozitOdkaz} className="btn-primary tap md:min-h-0" data-testid={`odkaz-uprava-ulozit-${o.id}`}>Uložit</button>
                          <button type="button" onClick={() => setUpravaOdkazu(null)} className="btn-ghost tap md:min-h-0">Zrušit</button>
                        </div>
                      </div>
                    )}
                    </React.Fragment>
                  );
                })}
                {novyOdkaz && (
                  <form onSubmit={pridatOdkaz} className="px-4 md:px-5 py-3.5 border-b border-line-soft space-y-2" data-testid="odkaz-formular">
                    <div className="grid grid-cols-1 md:grid-cols-[1fr_200px] gap-2">
                      <input className="field min-w-0" type="url" inputMode="url" autoFocus placeholder="Odkaz na soubor (https://…)"
                        value={novyOdkaz.url} onChange={(e) => setNovyOdkaz({ ...novyOdkaz, url: e.target.value })}
                        aria-label="Odkaz na soubor" data-testid="odkaz-url" />
                      <input className="field min-w-0" placeholder="Název (nepovinný)" value={novyOdkaz.nazev}
                        onChange={(e) => setNovyOdkaz({ ...novyOdkaz, nazev: e.target.value })} aria-label="Název" data-testid="odkaz-nazev" />
                    </div>
                    <KdoUvidi komu={novyOdkaz.komu} onKomu={(komu) => setNovyOdkaz({ ...novyOdkaz, komu })}
                      ai={novyOdkaz.ai} onAi={(ai) => setNovyOdkaz({ ...novyOdkaz, ai })} testid="odkaz-kdo" />
                    <div className="flex items-center gap-2">
                      <button type="submit" disabled={!novyOdkaz.url.trim()} className="btn-primary tap md:min-h-0" data-testid="odkaz-ulozit">Přidat odkaz</button>
                      <button type="button" onClick={() => { setNovyOdkaz(null); setChyba(""); }} className="btn-ghost tap md:min-h-0">Zrušit</button>
                    </div>
                  </form>
                )}
                {soubory.map((d) => {
                  const barva = DOK_BARVA[d.pripona] || { bg: "#f2f2ef", col: "#5c6169" };
                  return (
                    <div key={d.id} className="px-4 md:px-5 py-3 flex items-center gap-3 border-b border-line-soft"
                      data-testid={`soubor-${d.id}`}>
                      <span className="h-9 w-9 rounded-[10px] amount text-[9.5px] font-700 flex items-center justify-center shrink-0"
                        style={{ background: barva.bg, color: barva.col }}>
                        {d.pripona}
                      </span>
                      <a href={`${backend}${d.url}`} target="_blank" rel="noreferrer" className="min-w-0 flex-1">
                        <span className="block t-telo md:text-[13.5px] font-600 text-ink truncate">{d.nazev}</span>
                        <span className="block amount t-meta text-muted-2">
                          {velikost(d.velikost)} · {datumRok(d.created_at)}
                        </span>
                      </a>
                      {canWrite && (
                        <button onClick={() => smazSoubor(d.id)} data-testid={`del-soubor-${d.id}`} aria-label="Smazat dokument"
                          className="tap md:min-h-0 md:min-w-0 text-muted-2 hover:text-danger shrink-0"><X size={15} /></button>
                      )}
                    </div>
                  );
                })}
                {(muzeOdkazy || canWrite) && (
                  <div className="px-4 md:px-5 py-3 flex flex-wrap items-center gap-x-4 gap-y-1">
                    {muzeOdkazy && !novyOdkaz && (
                      <button type="button" onClick={() => setNovyOdkaz({ url: "", nazev: "", komu: [], ai: false })} data-testid="odkaz-pridat"
                        className="tap md:min-h-0 t-meta font-700 text-accent inline-flex items-center gap-1.5">
                        <Plus size={14} /> Odkaz na soubor
                      </button>
                    )}
                    {/* Nahrání do aplikace zůstává pro drobnosti — velké a sdílené soubory patří na disk firmy. */}
                    {canWrite && (
                      <button type="button" onClick={nahratSoubor} disabled={nahrava} data-testid="soubor-nahrat"
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => { e.preventDefault(); nahrajSoubory(e.dataTransfer.files); }}
                        className="tap md:min-h-0 t-meta font-600 text-muted hover:text-ink inline-flex items-center gap-1.5">
                        <Upload size={14} /> {nahrava ? "Nahrávám…" : "Nahrát malý soubor (max 25 MB)"}
                      </button>
                    )}
                  </div>
                )}
                {chyba && <div className="px-4 md:px-5 pb-3 t-meta text-danger" data-testid="soubor-chyba">{chyba}</div>}
                {!muzeOdkazy && !canWrite && soubory.length === 0 && odkazy.length === 0 && !p.slozka_url && !p.client?.slozka_url && (
                  <div className="px-5 py-6 t-meta text-muted text-center">Zatím žádné dokumenty.</div>
                )}
              </div>
            )}

            {aktivniZalozka === "historie" && (
              <div className="rise px-4 md:px-5 py-3">
                {p.udalosti.length === 0 ? (
                  <div className="py-4 t-meta text-muted text-center">Zatím nic.</div>
                ) : (
                  <ol className="flex flex-col">
                    {p.udalosti.map((e) => (
                      <li key={e.id} className="flex gap-2.5 py-2" data-testid={`udalost-${e.id}`}>
                        <span className={`h-2 w-2 rounded-full shrink-0 mt-[6px] ${
                          e.typ === "FAZE" ? "bg-accent" : e.typ === "SOUBOR" ? "bg-[#4f8ef0]"
                            : e.typ === "SYSTEM" ? "bg-muted-2" : "bg-success"}`} />
                        <span className="min-w-0">
                          <span className="block t-telo md:text-[13.5px] font-600 text-ink">{e.text}</span>
                          <span className="block amount t-meta text-muted-2">{fmtDateTime(e.created_at)}</span>
                        </span>
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            )}
          </KartaZalozky>
          <input ref={souborRef} type="file" multiple className="hidden"
            onChange={(e) => { nahrajSoubory(e.target.files); e.target.value = ""; }} />
        </div>
      </div>

      {addTech && <TechForm onClose={() => setAddTech(false)} onAdd={addTechnologie} />}
      {newAct && <NovaAktivita defaultClientId={p.client_id} pripadId={id}
        onClose={() => setNewAct(false)} onSaved={load} />}
      {detailId && (
        <AktivitaDetail id={detailId} onClose={() => setDetailId(null)}
          onEdit={(a) => { setDetailId(null); setEditAct(a); }}
          onChanged={load} />
      )}
      {editAct && (
        <NovaAktivita aktivita={editAct} onClose={() => setEditAct(null)}
          onSaved={() => { setEditAct(null); load(); }} />
      )}
      {swipe.supliky}
      {uprava && <PripadForm pripad={p} client={p.client} onClose={() => setUprava(false)} onSaved={load} />}
      <PdfCeka zobrazit={pdfCeka} />
      <PdfPripraveno url={pdfOdkaz} nazev="Souhrn případu" onClose={() => setPdfOdkaz(null)} />
    </div>
  );
}
